import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { NotificationConnectionState, NotificationPayload, VendingStepData } from "../../model/notifications/notifications";

interface NotificationState {
	connectionState: NotificationConnectionState;
	notifications: NotificationPayload[];
	vendingUpdates: VendingStepData[];
	unreadCount: number;
	lastError: string | null;
}

const initialState: NotificationState = {
	connectionState: NotificationConnectionState.Disconnected,
	notifications: [],
	vendingUpdates: [],
	unreadCount: 0,
	lastError: null,
};

export const notificationSlice = createSlice({
	name: "notifications",
	initialState,
	reducers: {
		connectionStateChanged: (state, action: PayloadAction<NotificationConnectionState>) => {
			state.connectionState = action.payload;
			if (action.payload === NotificationConnectionState.Failed) {
				state.lastError = "Connection failed";
			} else {
				state.lastError = null;
			}
		},
		notificationReceived: (state, action: PayloadAction<NotificationPayload>) => {
			state.notifications.unshift(action.payload);
			state.unreadCount += 1;

			// Keep only last 50 notifications in memory
			if (state.notifications.length > 50) {
				state.notifications = state.notifications.slice(0, 50);
			}
		},
		vendingStatusReceived: (state, action: PayloadAction<VendingStepData>) => {
			state.vendingUpdates.unshift(action.payload);

			// Keep only last 20 vending updates in memory
			if (state.vendingUpdates.length > 20) {
				state.vendingUpdates = state.vendingUpdates.slice(0, 20);
			}
		},
		markNotificationsAsRead: state => {
			state.unreadCount = 0;
		},
		clearNotifications: state => {
			state.notifications = [];
			state.unreadCount = 0;
		},
		setUnreadCount: (state, action: PayloadAction<number>) => {
			state.unreadCount = action.payload;
		},
		decrementUnreadCount: state => {
			state.unreadCount = Math.max(0, state.unreadCount - 1);
		},
		clearVendingUpdates: state => {
			state.vendingUpdates = [];
		},
		connectionError: (state, action: PayloadAction<string>) => {
			state.lastError = action.payload;
			state.connectionState = NotificationConnectionState.Failed;
		},
	},
});

export const {
	connectionStateChanged,
	notificationReceived,
	vendingStatusReceived,
	markNotificationsAsRead,
	setUnreadCount,
	decrementUnreadCount,
	clearNotifications,
	clearVendingUpdates,
	connectionError,
} = notificationSlice.actions;

export default notificationSlice.reducer;